import React from 'react';
import { BaseModal } from '../BaseModal';
import type { OnboardingSystem, ColumnGroups } from '../../pages/CatalogPage';

type OnboardingCountry = {
  country: string;
  countryName: string;
  countryFlag: string;
  systems: OnboardingSystem[];
};

type OnboardingModalProps = {
  country: OnboardingCountry;
  columnGroups: ColumnGroups;
  onClose: () => void;
};

type SystemSection = {
  label: string;
  columns: ColumnGroups[keyof ColumnGroups];
};

function OnboardingSection({ title, columns, system }: {
  title: string;
  columns: ColumnGroups[keyof ColumnGroups];
  system: OnboardingSystem | undefined;
}) {
  if (!columns.length) return null;

  const items = columns
    .filter(col => system?.[col.key as keyof OnboardingSystem] === 'Y')
    .map(col => col.label);

  if (items.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
    </div>
  );
};

export function OnboardingModal({
  country,
  columnGroups,
  onClose,
}: OnboardingModalProps) {
  const system = country.systems[0];

  const sections: SystemSection[] = [
    { label: 'Boarding Systems', columns: columnGroups.boardingSystems },
    { label: 'Underwriting Systems', columns: columnGroups.underwritingSystems },
    { label: 'Merchant Portal', columns: columnGroups.merchantPortal },
    { label: 'Partner Portal', columns: columnGroups.partnerPortal },
    { label: 'Chargeback Management', columns: columnGroups.chargebackManagement },
    { label: 'Monthly Statements', columns: columnGroups.monthlyStatements },
    { label: 'Live Transactions', columns: columnGroups.liveTransactions },
  ];

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title={`${country.countryFlag} ${country.countryName}`}
      subtitle="Onboarding & Operational Systems"
      maxWidth="5xl"
    >
      <div className="grid grid-cols-2 gap-6">
        {sections.map(section => (
          <OnboardingSection
            key={section.label}
            title={section.label}
            columns={section.columns}
            system={system}
          />
        ))}
      </div>
    </BaseModal>
  );
};
